import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// Simplified types for identity module
export type SSOProtocol = 'oidc' | 'saml';

export interface SSOConfiguration {
  id: string;
  organization_id: string;
  protocol: SSOProtocol;
  provider: string;
  display_name: string | null;
  client_id: string | null;
  issuer_url: string | null;
  allowed_domains: string[] | null;
  is_enabled: boolean;
  auto_provision_users: boolean;
  default_role: string;
  created_at: string;
  updated_at: string;
  organization?: {
    id: string;
    name: string;
    slug: string;
  };
}

export interface SimpleSSOConfigurationFormData {
  organization_id: string;
  protocol?: SSOProtocol;
  provider: string;
  display_name?: string;
  client_id?: string;
  issuer_url?: string;
  allowed_domains: string[];
  is_enabled: boolean;
  auto_provision_users: boolean;
  default_role: string;
}

export function useSSOConfigurations() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch all SSO configurations with organization data
  const {
    data: configurations = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["sso-configurations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sso_configurations")
        .select(`
          *,
          organization:organizations(*)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as SSOConfiguration[];
    },
  });

  // Create SSO configuration
  const createConfiguration = useMutation({
    mutationFn: async (formData: SimpleSSOConfigurationFormData) => {
      const { data, error } = await supabase
        .from("sso_configurations")
        .insert({
          organization_id: formData.organization_id,
          protocol: formData.protocol || 'oidc',
          provider: formData.provider,
          client_id: formData.client_id,
          issuer_url: formData.issuer_url,
          allowed_domains: formData.allowed_domains,
          is_enabled: formData.is_enabled,
          auto_provision_users: formData.auto_provision_users,
          default_role: formData.default_role,
        })
        .select(`
          *,
          organization:organizations(*)
        `)
        .single();

      if (error) throw error;
      return data as SSOConfiguration;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sso-configurations"] });
      toast({
        title: "SSO configuration created",
        description: "The SSO configuration has been created successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating SSO configuration",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update SSO configuration
  const updateConfiguration = useMutation({
    mutationFn: async ({ id, ...formData }: SimpleSSOConfigurationFormData & { id: string }) => {
      const { data, error } = await supabase
        .from("sso_configurations")
        .update({
          organization_id: formData.organization_id,
          protocol: formData.protocol || 'oidc',
          provider: formData.provider,
          client_id: formData.client_id,
          issuer_url: formData.issuer_url,
          allowed_domains: formData.allowed_domains,
          is_enabled: formData.is_enabled,
          auto_provision_users: formData.auto_provision_users,
          default_role: formData.default_role,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select(`
          *,
          organization:organizations(*)
        `)
        .single();

      if (error) throw error;
      return data as SSOConfiguration;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sso-configurations"] });
      toast({
        title: "SSO configuration updated",
        description: "The SSO configuration has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating SSO configuration",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete SSO configuration
  const deleteConfiguration = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("sso_configurations")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sso-configurations"] });
      toast({
        title: "SSO configuration deleted",
        description: "The SSO configuration has been deleted successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting SSO configuration",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Toggle SSO configuration enabled status
  const toggleConfigurationStatus = useMutation({
    mutationFn: async ({ id, is_enabled }: { id: string; is_enabled: boolean }) => {
      const { data, error } = await supabase
        .from("sso_configurations")
        .update({
          is_enabled,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select(`
          *,
          organization:organizations(*)
        `)
        .single();

      if (error) throw error;
      return data as SSOConfiguration;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["sso-configurations"] });
      toast({
        title: data.is_enabled ? "SSO enabled" : "SSO disabled",
        description: `SSO for ${data.allowed_domains?.join(", ") || "this organization"} has been ${data.is_enabled ? "enabled" : "disabled"}.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating SSO status",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    configurations,
    isLoading,
    error,
    refetch,
    createConfiguration,
    updateConfiguration,
    deleteConfiguration,
    toggleConfigurationStatus,
  };
}

// Utility function to validate domain format
export function isValidDomain(domain: string): boolean {
  const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
  return domainRegex.test(domain);
}

// Utility function to extract domain from email
export function extractDomainFromEmail(email: string): string | null {
  const parts = email.split("@");
  if (parts.length !== 2) return null;
  return parts[1].toLowerCase();
}
